import { format } from 'date-fns';

import { getGmtOffsetString } from './getGmtOffsetString';

/**
 * ISO date format.
 * @example '2024-09-12'
 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * ISO date and time format.
 * @example '2024-09-12T15:30:45Z'
 */
export const ISO_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";

/**
 * Local date format.
 * @example '12.09.2024'
 */
export const DATE_FORMAT = 'dd.MM.yyyy';

/**
 * Date and time format with seconds.
 * @example '12.09.2024 15:30:45'
 */
export const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm:ss';

/**
 * Date and time format without seconds.
 * @example '12.09.2024 15:30'
 */
export const DATE_TIME_SHORT_FORMAT = 'dd.MM.yyyy HH:mm';

/**
 * Time format with seconds.
 * @example '15:30:45'
 */
export const TIME_FORMAT = 'HH:mm:ss';

/**
 * Time format without seconds.
 * @example '15:30'
 */
export const TIME_SHORT_FORMAT = 'HH:mm';

/**
 * Formats a given date to ISO format (yyyy-MM-dd).
 * @example '2024-09-12'
 */
export const formatToISODate = (date: Date): string =>
  format(date, ISO_DATE_FORMAT);

/**
 * Formats a given date to ISO date and time format (yyyy-MM-ddTHH:mm:ssZ).
 * @example '2024-09-12T15:30:45Z'
 */
export const formatToISODateTime = (date: Date): string =>
  format(date, ISO_DATE_TIME_FORMAT);

/**
 * Formats a given date to local format (dd.MM.yyyy).
 * @example '12.09.2024'
 */
export const formatToDate = (date: Date): string => format(date, DATE_FORMAT);

/**
 * Formats a given date to date and time format with seconds (dd.MM.yyyy HH:mm:ss).
 * @example '12.09.2024 15:30:45'
 */
export const formatToDateTime = (date: Date): string =>
  format(date, DATE_TIME_FORMAT);

/**
 * Formats a given date to date and time format without seconds (dd.MM.yyyy HH:mm).
 * @example '12.09.2024 15:30'
 */
export const formatToDateTimeShort = (date: Date): string =>
  format(date, DATE_TIME_SHORT_FORMAT);

/**
 * Formats a given date to time format with seconds (HH:mm:ss).
 * @example '15:30:45'
 */
export const formatToTime = (date: Date): string => format(date, TIME_FORMAT);

/**
 * Formats a given date to time format without seconds (HH:mm).
 * @example '15:30'
 */
export const formatToTimeShort = (date: Date): string =>
  format(date, TIME_SHORT_FORMAT);

export const formatWithGmtOffset = (
  date: Date,
  gmtOffset: number,
  formatFunc: (date: Date) => string
): string => {
  const gmtOffsetString = getGmtOffsetString(gmtOffset);
  return `${formatFunc(date)} ${gmtOffsetString}`;
};

// Sets the time of the given date to the start of the day (00:00:00.000)
export const setStartOfDay = (date: Date) => {
  date.setHours(0, 0, 0, 0);
  return date;
};

// Sets the time of the given date to the end of the day (23:59:59.999)
export const setEndOfDay = (date: Date) => {
  date.setHours(23, 59, 59, 999);
  return date;
};
/**
 * Converts a given date to UTC time.
 */
export const getUtcTime = (date: Date): Date => {
  const d = new Date(date);
  d.setTime(d.getTime() + d.getTimezoneOffset() * 60000);
  return d;
};

/**
 * Converts a given date to the specified timezone.
 * @returns {Date} The date converted to the given timezone.
 */
export const convertDateByTimezone = (date: Date, gmtOffset: number): Date => {
  const utcDate = getUtcTime(date);
  utcDate.setTime(utcDate.getTime() + gmtOffset * 1000);
  return utcDate;
};

/**
 * Shifts a given date by the specified GMT offset, preserving the timezone.
 * @param date - The original date.
 * @param gmtOffset - The GMT offset in seconds to shift (can be negative).
 * @returns {Date} The new date.
 */
export const shiftDateByGmtOffset = (date: Date, gmtOffset: number): Date =>
  new Date(date.getTime() - gmtOffset * 1000);
